<script setup>
import SendTestEmailAction from '@frontJs/components/actions/SendTestEmailAction.vue';
import ManageReportVisibilityAction from '@frontJs/components/actions/projectManagement/customers/ManageReportVisibilityAction.vue';
import ManageReportVisiblePeriodsAction from '@frontJs/components/actions/projectManagement/customers/ManageReportVisiblePeriodsAction.vue';
import ArrowSquareOutIcon from '@iconify-vue/ph/arrow-square-out';
import CalendarIcon from '@iconify-vue/ph/calendar-blank';
import EnvelopeIcon from '@iconify-vue/ph/envelope';
import EyeIcon from '@iconify-vue/ph/eye';
import SendEmailAction from '@js/components/actions/SendEmailAction.vue';
import ActionButton from '@js/components/buttons/ActionButton.vue';
import DataTableLayout from '@js/components/tables/DataTableLayout.vue';
import BaseCell from '@js/components/tables/cells/BaseCell.vue';
import HeaderCell from '@js/components/tables/cells/HeaderCell.vue';
import PillCell from '@js/components/tables/cells/PillCell.vue';
import TextCell from '@js/components/tables/cells/TextCell.vue';
import { useDataTable } from '@js/utils/dataTable';
import { useRoute } from '@js/utils/route';
import { toastSuccess } from '@js/utils/toast';
import { createColumnHelper } from '@tanstack/vue-table';
import { h } from 'vue';

const { withReport } = defineProps({
    withReport: {
        type: Boolean,
        required: false,
    },
});

const { route } = useRoute();

const columnHelper = createColumnHelper();

function openPreview(tenant, tab) {
    const params = new URLSearchParams({
        token: tenant.superAdminLoginToken,
        redirect: 'app.apikom.report',
        'redirectParams[tab]': tab,
        'redirectParams[preview]': '1',
    });

    window.open(
        '/' + tenant.id + '/login?' + params.toString(),
        'reportPreview',
        'popup,width=1280,height=900,scrollbars=yes,resizable=yes',
    );
}

function formatPeriodCount(periodType, count) {
    return periodType === 'quarter'
        ? `${count} trimestre${count > 1 ? 's' : ''}`
        : `${count} mois`;
}

function renderVisibilityCell(tenant, periodType, validations, updateData) {
    const validatedPeriods = (validations ?? []).filter(
        (validation) => validation.validated,
    );

    return h(
        PillCell,
        {
            pillValue: validatedPeriods.length
                ? `${formatPeriodCount(periodType, validatedPeriods.length)} · dernier : ${validatedPeriods[0].formattedPeriod}`
                : 'Aucun',
            type: validatedPeriods.length ? 'success' : 'danger',
        },
        {
            actions: () =>
                h(
                    ManageReportVisibilityAction,
                    {
                        tenant,
                        periodType,
                        validations: validations ?? [],
                        callback: () => updateData(),
                    },
                    {
                        trigger: () =>
                            h(ActionButton, {
                                size: 'small',
                                square: true,
                                title:
                                    periodType === 'quarter'
                                        ? 'Gérer la visibilité des rapports trimestriels'
                                        : 'Gérer la visibilité des rapports mensuels',
                                icon: EyeIcon,
                                type: 'secondary',
                            }),
                    },
                ),
        },
    );
}

function renderVisiblePeriodsCell(tenant, periodType, periods, updateData) {
    return h(
        TextCell,
        {
            text: formatPeriodCount(periodType, periods),
        },
        {
            actions: () =>
                h(
                    ManageReportVisiblePeriodsAction,
                    {
                        tenant,
                        periodType,
                        periods,
                        callback: () => updateData(),
                    },
                    {
                        trigger: () =>
                            h(ActionButton, {
                                size: 'small',
                                square: true,
                                title:
                                    periodType === 'quarter'
                                        ? 'Modifier le nombre de trimestres de rapports visibles'
                                        : 'Modifier le nombre de mois de rapports visibles',
                                icon: CalendarIcon,
                                type: 'secondary',
                            }),
                    },
                ),
        },
    );
}

function renderSendEmailAction(tenant, periodType, content, updateData) {
    return h(
        SendEmailAction,
        {
            fromOptions: tenant.email.fromOptions,
            toOptions: tenant.email.toOptions,
            to: tenant.email.toOptions,
            subject: content.subject,
            body: content.body,
            callback: (from, to, subject, body, setLoading, setIsOpen) => {
                axios
                    .post(
                        route(
                            'front.projectManagement.customer.postSendReportEmail',
                            { tenant: tenant.id },
                        ),
                        { periodType, from, to, subject, body },
                    )
                    .then(() => {
                        setLoading(false);
                        setIsOpen(false);
                        toastSuccess('Envoyé avec succès.');
                        updateData();
                    })
                    .catch((error) => {
                        console.error(error);
                    });
            },
        },
        {
            trigger: () =>
                h(
                    ActionButton,
                    {
                        size: 'small',
                        title:
                            periodType === 'quarter'
                                ? 'Envoyer le rapport trimestriel'
                                : 'Envoyer le rapport mensuel',
                        leftIcon: EnvelopeIcon,
                        type: 'secondary',
                    },
                    () =>
                        periodType === 'quarter' ? 'Trimestriel' : 'Mensuel',
                ),
            'footer-actions': ({ from, subject, body }) =>
                h(
                    SendTestEmailAction,
                    {
                        send: (email) =>
                            axios.post(
                                route(
                                    'front.projectManagement.customer.postSendReportTestEmail',
                                ),
                                { from, subject, body, email },
                            ),
                    },
                    {
                        trigger: () =>
                            h(
                                ActionButton,
                                { type: 'secondary' },
                                () => 'Envoyer un test',
                            ),
                    },
                ),
        },
    );
}

const {
    loading,
    table,
    count,
    globalFilter,
    setGlobalFilter,
    availableFilters,
    filters,
    setFilters,
    updateData,
} = useDataTable({
    columns: [
        columnHelper.accessor(() => null, {
            id: 'name',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Nom',
                }),
            cell: (props) =>
                h(TextCell, {
                    text: props.row.original.name,
                }),
        }),
        columnHelper.accessor(() => null, {
            id: 'lastLoginDate',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Dernière connexion',
                }),
            cell: (props) =>
                props.row.original.lastLoginDate
                    ? h(TextCell, {
                          text: props.row.original.lastLoginDate,
                      })
                    : null,
        }),
        columnHelper.accessor(() => null, {
            id: 'maxMonthlySentAt',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Date max envoyée (mensuel)',
                }),
            cell: (props) =>
                props.row.original.maxMonthlySentAt
                    ? h(TextCell, {
                          text: props.row.original.maxMonthlySentAt,
                      })
                    : null,
        }),
        columnHelper.accessor(() => null, {
            id: 'maxQuarterlySentAt',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Date max envoyée (trimestriel)',
                }),
            cell: (props) =>
                props.row.original.maxQuarterlySentAt
                    ? h(TextCell, {
                          text: props.row.original.maxQuarterlySentAt,
                      })
                    : null,
        }),
        columnHelper.accessor(() => null, {
            id: 'monthlyVisibleToClient',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Visible par le client (mensuel)',
                }),
            cell: (props) =>
                renderVisibilityCell(
                    props.row.original,
                    'month',
                    props.row.original.monthlyReportValidations,
                    updateData,
                ),
        }),
        columnHelper.accessor(() => null, {
            id: 'quarterlyVisibleToClient',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Visible par le client (trimestriel)',
                }),
            cell: (props) =>
                renderVisibilityCell(
                    props.row.original,
                    'quarter',
                    props.row.original.quarterlyReportValidations,
                    updateData,
                ),
        }),
        columnHelper.accessor(() => null, {
            id: 'visibleReportMonths',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Mois visibles',
                }),
            cell: (props) =>
                renderVisiblePeriodsCell(
                    props.row.original,
                    'month',
                    props.row.original.numberOfVisibleReportMonths ?? 2,
                    updateData,
                ),
        }),
        columnHelper.accessor(() => null, {
            id: 'visibleReportQuarters',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Trimestres visibles',
                }),
            cell: (props) =>
                renderVisiblePeriodsCell(
                    props.row.original,
                    'quarter',
                    props.row.original.numberOfVisibleReportQuarters ?? 4,
                    updateData,
                ),
        }),
        columnHelper.display({
            id: 'actions',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Actions',
                    width: 0,
                }),
            cell: (props) =>
                h(
                    BaseCell,
                    {},
                    {
                        default: () => [
                            h(ActionButton, {
                                size: 'small',
                                square: true,
                                title: 'Prévisualiser',
                                callback: () =>
                                    openPreview(
                                        props.row.original,
                                        'apikomMonthlyServices',
                                    ),
                                icon: ArrowSquareOutIcon,
                                type: 'secondary',
                            }),
                            renderSendEmailAction(
                                props.row.original,
                                'month',
                                props.row.original.email.monthly,
                                updateData,
                            ),
                            renderSendEmailAction(
                                props.row.original,
                                'quarter',
                                props.row.original.email.quarterly,
                                updateData,
                            ),
                        ],
                    },
                ),
        }),
    ],
    url: route('front.projectManagement.customer.postReports'),
    columnPinning: {
        right: ['actions'],
    },
    data: {
        withReport,
    },
    columnVisibility: {
        maxMonthlySentAt: withReport,
        maxQuarterlySentAt: withReport,
        monthlyVisibleToClient: withReport,
        quarterlyVisibleToClient: withReport,
        visibleReportMonths: withReport,
        visibleReportQuarters: withReport,
        actions: withReport,
    },
});
</script>

<template>
    <DataTableLayout
        :table="table"
        :count="count"
        :global-filter="globalFilter"
        :set-global-filter="setGlobalFilter"
        :available-filters="availableFilters"
        :filters="filters"
        :set-filters="setFilters"
        :loading="loading"
        force-scroll
    />
</template>
